import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

const links = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

function Wordmark({ light }) {
  return (
    <Link to="/" className={`font-serif text-[1.35rem] font-medium transition-colors ${light ? 'text-paper' : 'text-ink'}`}>
      Address<span className="text-brass-light">Collective</span>
    </Link>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    setOpen(false);
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Header floats transparent over the dark hero, then turns solid on scroll.
  const light = !scrolled && !open;

  const linkClass = ({ isActive }) =>
    `relative pb-1 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-left after:bg-brass-light after:transition-transform after:duration-300 hover:after:scale-x-100 ${
      isActive ? 'after:scale-x-100' : 'after:scale-x-0'
    }`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          light ? 'border-b border-transparent bg-transparent' : 'border-b border-ink/15 bg-stone/95 backdrop-blur'
        }`}
      >
        <nav className="wrap flex h-[84px] items-center justify-between">
          <Wordmark light={light} />
          <div className={`hidden gap-10 text-[0.95rem] font-medium transition-colors md:flex ${light ? 'text-paper' : 'text-ink'}`}>
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>{l.label}</NavLink>
            ))}
          </div>
          <Link
            to="/contact#enquiry-form"
            className={`hidden rounded-sm border px-5 py-2.5 text-sm font-medium transition-colors md:inline-block ${
              light ? 'border-paper/70 text-paper hover:bg-paper hover:text-ink' : 'border-ink text-ink hover:bg-ink hover:text-paper'
            }`}
          >
            Book a consultation
          </Link>
          <button
            className={`text-2xl leading-none md:hidden ${light ? 'text-paper' : 'text-ink'}`}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? '✕' : '☰'}
          </button>
        </nav>
        {open && (
          <div className="flex flex-col border-t border-ink/15 bg-stone px-6 pb-6 md:hidden">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className="border-b border-ink/15 py-4 aria-[current=page]:font-medium">
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact#enquiry-form" className="btn btn-primary mt-4 text-center">Book a consultation</Link>
          </div>
        )}
      </header>

      <main><Outlet /></main>

      {scrolled && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
          className="fixed bottom-6 left-6 z-40 flex h-12 w-12 items-center justify-center rounded-sm border border-paper/60 bg-ink text-2xl leading-none text-paper shadow-lg transition-colors hover:border-brass-light hover:bg-brass-light hover:text-ink"
        >
          ↑
        </button>
      )}

      <footer className="bg-ink pb-10 pt-20 text-paper">
        <div className="wrap">
          <div className="grid gap-12 md:grid-cols-[1.4fr_1fr]">
            <div>
              <Wordmark light />
              <p className="mt-5 max-w-[34ch] text-[0.95rem] text-sand/80">A curated portfolio of homes and investments across Dubai, represented with the care of a long-term relationship.</p>
            </div>
            <div>
              <h3 className="eyebrow mb-5 font-sans text-brass-light">Explore</h3>
              <ul className="space-y-3 text-[0.95rem] text-sand">
                {links.map((l) => <li key={l.to}><Link to={l.to} className="transition-colors hover:text-paper">{l.label}</Link></li>)}
              </ul>
            </div>
          </div>
          <div className="mt-16 flex flex-wrap justify-between gap-3 border-t border-paper/15 pt-6 text-[0.82rem] text-sand/60">
            <span>© {new Date().getFullYear()} Address Collective. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </>
  );
}
